import dayjs from "dayjs";
import { ITimeProvider } from "../driven/providers/provider";
import { analyticsController } from "../../../AppController";

export const ReportControllerFactory = ({
  reportService,
  timeProvider,
}: {
  reportService: IReportService;
  timeProvider: ITimeProvider;
}): IReportController => {
  const getAIRiskReport: IGetAIRiskReport = async ({
    clinicId,
    date,
    timezone,
    token,
    user,
    facilityName,
  }) => {
    const [report, reAdmitRadar] = await Promise.all([
      reportService.getRiskReport({ clinic_id: clinicId, date, token }),
      reportService.getReadmitRadar({ clinic_id: clinicId, date, token }),
    ]);
    if (
      !report.success ||
      !report.data?.data ||
      (!reAdmitRadar.success && !reAdmitRadar.data?.data)
    ) {
      analyticsController.logEvent("Morning Report Failed to Load", {
        email: user?.email,
        selectedFacility: facilityName,
        selectedFacilityId: clinicId,
        cUserId: user?.userID,
      });
      return {
        success: false,
        error: {
          message:
            (report.data as any)?.detail?.message === "report has been expired"
              ? (report.data as any)?.detail?.message
              : "Report not found for this date!",
        },
      };
    }

    analyticsController.logEvent("Morning Report Loaded Successfully", {
      email: user?.email,
      selectedFacility: facilityName,
      selectedFacilityId: clinicId,
      cUserId: user?.userID,
    });
    const parsedReadmitRadar =
      reAdmitRadar?.data?.data?.sort(
        (a, b) => a.current_rank - b.current_rank
      ) || [];

    const hasAll = parsedReadmitRadar.some(
      (item) => item.section_name === "ALL"
    );
    const hasLOS21 = parsedReadmitRadar.some(
      (item) => item.section_name === "LOS21"
    );
    const parsed21DayRR = parsedReadmitRadar
      .filter((item) => {
        if (hasLOS21) {
          return item.section_name === "LOS21";
        }
        return item.section_name === "LOS30";
      })
      .filter((item) => item.is_deleted !== 1);
    const parsedAllDayRR = parsedReadmitRadar
      .filter((item) => {
        if (hasAll) {
          return item.section_name === "ALL";
        }
        return item.section_name === "LOS30";
      })
      .filter((item) => item.is_deleted !== 1);

    const parsed3DayRR = parsedReadmitRadar
      .filter((item) => item.section_name === "LOS3")
      // .slice(0, reAdmitRadarCount)
      .filter((val) => val.is_deleted !== 1);

    const parsed24HoursRR = parsedReadmitRadar
      .filter((item) => item.section_name === "COC24")
      // .slice(0, reAdmitRadarCount)
      .filter((val) => val.is_deleted !== 1);

    const footerTimestamp = timeProvider.dateLibrary
      .utc(
        report.data?.data?.meta_data?.created_at ||
          reAdmitRadar.data?.data.length
          ? reAdmitRadar.data?.data[0].triggered_datetime
          : ""
      )
      .tz(timezone !== "None" ? timezone : dayjs.tz.guess())
      .format("MM/DD/YYYY HH:mm z");

    const headerTimestamp = timeProvider
      .dateLibrary(
        report.data?.data?.meta_data?.report_generated ||
          reAdmitRadar.data?.data.length
          ? reAdmitRadar.data?.data[0].triggered_datetime
          : "",
        "YYYY-MM-DD"
      )
      .format("MM/DD/YYYY");
    return {
      success: true,
      data: {
        parsed21DayRR,
        parsedAllDayRR,
        parsed3DayRR,
        parsed24HoursRR,
        report: report.data!.data,
        preference: {},
        headerTimestamp,
        footerTimestamp,
      },
    };
  };

  const getReAdmitRadar: IGetReAdmitRadar = async ({ clinicId, date }) => {
    const reAdmitRadar = await reportService.getReadmitRadar({
      clinic_id: clinicId,
      date,
    });

    if (!reAdmitRadar.success && !reAdmitRadar.data?.data) {
      return {
        success: false,
        error: { message: "Readmit Radar not found for this date!" },
      };
    }
    const parsedReadmitRadar =
      reAdmitRadar?.data?.data?.sort(
        (a, b) => a.current_rank - b.current_rank
      ) || [];

    const parsed30DayRR = parsedReadmitRadar
      .filter((item) => item.section_name === "LOS30")
      .slice(0, 10);

    return {
      success: true,
      data: parsed30DayRR,
    };
  };

  const getReadmitRadarMostCritical: IGetReAdmitRadar = async ({
    clinicId,
    date,
    floorIds,
  }) => {
    const reAdmitRadar = await reportService.getReadmitRadarMostCritical({
      clinic_id: clinicId,
      date,
      floorIds,
    });

    if (!reAdmitRadar.success && !reAdmitRadar.data?.data) {
      return {
        success: false,
        error: { message: "Readmit Radar not found for this date!" },
      };
    }
    const parsedReadmitRadar =
      reAdmitRadar?.data?.data?.sort(
        (a, b) => a.current_rank - b.current_rank
      ) || [];

    return {
      success: true,
      data: parsedReadmitRadar,
    };
  };

  const getRiskReportList: IGetRiskReportList = async ({
    clinicId,
    startDate,
    endDate,
    timezone,
  }) => {
    const response = await reportService.getRiskReportList({
      clinic_id: clinicId,
      start_date: startDate,
      end_date: endDate,
    });

    if (response.success && response.data && response.data.data) {
      const resData = response.data.data.map((item) => {
        return {
          ...item,
          report_generated: timeProvider.dateLibrary
            .utc(item.report_generated)
            .format("MM-DD-YYYY")!,
          time_queued: timeProvider
            .convertIntoLocalDateTime(item.time_queued)!
            .format("MM/DD/YYYY hh:mm a"),
          created_at: timeProvider.dateLibrary
            .utc(item.created_at)
            .tz(timezone)
            .format("MM/DD/YYYY hh:mm a"),
        };
      });
      return {
        success: true,
        data: resData,
      };
    }

    return {
      success: false,
      error: { message: "Something went wrong!" },
    };
  };

  const putAckRiskReport: IPutRiskReport = async ({
    clinicId,
    idtReportId,
  }) => {
    const response = await reportService.putAckRiskReportService({
      clinic_id: clinicId,
      idt_report_id: idtReportId,
    });

    if (response.success) {
      return {
        success: true,
        data: {},
      };
    }

    return {
      success: false,
      error: { message: "Something went wrong!" },
    };
  };

  return {
    getDailyReportList: reportService.getDailyReportList,
    getDailyReport: reportService.getDailyReport,
    addReadDailyReport: reportService.addReadDailyReport,
    getAIRiskReport,
    getRiskReportList,
    getReAdmitRadar,
    getReadmitRadarMostCritical,
    putAckRiskReport,
  };
};
